import { useState, useEffect } from "react";
import { getUser, saveUser, getLocalEntries, clearAll } from "@/store";
import { usePush } from "@/hooks/usePush";

/* Reusable section title */
function SectionTitle({ children }) {
  return (
    <p style={{
      fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
      letterSpacing: "0.16em", color: "var(--teal)",
      textTransform: "uppercase", marginBottom: 12, marginTop: 0,
    }}>
      {children}
    </p>
  );
}

/* Reusable row — 44px min tap target */
function Row({ label, last, children }) {
  return (
    <div style={{
      display: "flex", alignItems: "center",
      justifyContent: "space-between",
      minHeight: 44,
      padding: "8px 0",
      borderBottom: last ? "none" : "1px solid rgba(29,158,117,0.1)",
      gap: 12,
    }}>
      <span style={{ fontSize: 16, color: "var(--cream)", flexShrink: 0 }}>
        {label}
      </span>
      {children}
    </div>
  );
}

/* Inline input — 16px prevents iOS zoom */
function InlineInput({ value, onChange, type = "text", width = 120, testId }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{
        fontSize: 16,
        background: "var(--teal-glow)",
        border: "1px solid var(--teal-border)",
        borderRadius: 6, padding: "6px 10px",
        height: 40, color: "var(--cream)", outline: "none",
        width, textAlign: "center",
        fontFamily: "var(--mono)", fontWeight: 300,
      }}
    />
  );
}

/* Action button */
function ActionBtn({ onClick, disabled, children, variant = "default", testId }) {
  const styles = {
    default: { background: "var(--teal-glow)", border: "1px solid var(--teal-border)", color: "var(--teal-lt)" },
    primary: { background: "var(--teal)", border: "none", color: "var(--deep)" },
    active: { background: "rgba(29,158,117,0.18)", border: "1px solid var(--teal)", color: "var(--teal-lt)" },
    danger: { background: "none", border: "1px solid rgba(226,75,74,0.3)", color: "rgba(226,75,74,0.65)" },
  };
  const s = styles[variant] || styles.default;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        ...s,
        width: "100%", height: 44, borderRadius: 8,
        fontFamily: "var(--mono)", fontSize: 13, fontWeight: 400,
        letterSpacing: "0.06em",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.18s", marginBottom: 8,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {children}
    </button>
  );
}

const PUSH_UI = {
  idle: { label: "", color: "var(--cream-faint)" },
  loading: { label: "Working...", color: "var(--cream-faint)" },
  unsupported: { label: "Not supported on this browser", color: "var(--cream-faint)" },
  denied: { label: "Blocked \u2014 enable in browser settings", color: "rgba(226,75,74,0.7)" },
  subscribed: { label: "Daily reminders are on", color: "var(--teal)" },
  unsubscribed: { label: "Not yet enabled", color: "var(--cream-faint)" },
  error: { label: "Something went wrong. Try again.", color: "rgba(226,75,74,0.7)" },
};

export default function Settings({ showToast }) {
  const [name, setName] = useState("");
  const [time, setTime] = useState("09:00");
  const [saving, setSaving] = useState(false);
  const [count, setCount] = useState(0);

  const { status: pushStatus, supported: pushSupported, subscribe, unsubscribe } = usePush();

  useEffect(() => {
    const u = getUser();
    setName(u.name || "");
    setTime(u.notifyTime || "09:00");
    setCount(getLocalEntries().length);
  }, []);

  function saveProfile() {
    setSaving(true);
    const updated = { name: name.trim() || "Friend", notifyTime: time };
    saveUser({ ...getUser(), ...updated });
    showToast("Saved.");
    setTimeout(() => setSaving(false), 400);
  }

  async function handlePushToggle() {
    if (pushStatus === "subscribed") {
      await unsubscribe();
      showToast("Daily reminders turned off.");
    } else {
      const result = await subscribe();
      if (result.ok) showToast(`Daily reminder set for ${time} UTC.`);
      else if (result.reason === "denied") showToast("Blocked \u2014 enable in browser settings.");
      else showToast("Could not enable notifications.");
    }
  }

  function exportData() {
    const data = { user: getUser(), entries: getLocalEntries(), exported: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "hypha-export.json"; a.click();
    URL.revokeObjectURL(url);
    showToast("Exported.");
  }

  function confirmClear() {
    if (!window.confirm("Delete all data permanently? Cannot be undone.")) return;
    clearAll();
    showToast("Data cleared.");
    setTimeout(() => window.location.reload(), 800);
  }

  const pushUI = PUSH_UI[pushStatus] || PUSH_UI.idle;
  const isSubscribed = pushStatus === "subscribed";
  const canToggle = pushSupported && !["unsupported", "denied", "loading"].includes(pushStatus);

  return (
    <div className="scroll-view scroll-view-padded" style={{ padding: "24px 20px 0" }} data-testid="settings-view">
      {/* Profile */}
      <section style={{ marginBottom: 32 }}>
        <SectionTitle>Profile</SectionTitle>
        <Row label="Your name">
          <InlineInput value={name} onChange={setName} width={130} testId="settings-name-input" />
        </Row>
        <Row label="Check-in time" last>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
            <InlineInput value={time} onChange={setTime} type="time" width={104} testId="settings-time-input" />
            <span style={{
              fontFamily: "var(--mono)", fontSize: 9, fontWeight: 300,
              color: "var(--cream-faint)", letterSpacing: "0.06em",
            }}>
              UTC \u2014 adjust for your timezone
            </span>
          </div>
        </Row>
        <div style={{ paddingTop: 14 }}>
          <ActionBtn onClick={saveProfile} disabled={saving} variant="primary" testId="settings-save-btn">
            {saving ? "Saving..." : "Save profile"}
          </ActionBtn>
        </div>
      </section>

      {/* Daily reminder */}
      <section style={{ marginBottom: 32 }}>
        <SectionTitle>Daily reminder</SectionTitle>
        <ActionBtn
          onClick={handlePushToggle}
          disabled={!canToggle}
          variant={isSubscribed ? "active" : "default"}
          testId="settings-push-toggle-btn"
        >
          {isSubscribed ? "Turn off reminder" : "Enable daily reminder"}
        </ActionBtn>
        {pushUI.label && (
          <p style={{
            fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
            color: pushUI.color, letterSpacing: "0.04em", lineHeight: 1.6,
            marginTop: -2, marginBottom: 8,
          }}>
            {pushUI.label}
          </p>
        )}
        <p style={{
          fontFamily: "var(--mono)", fontSize: 9, fontWeight: 300,
          color: "var(--cream-faint)", letterSpacing: "0.04em", lineHeight: 1.5,
        }}>
          Reminder time is UTC. New York = subtract 4-5hrs, London = same or +1, Mumbai = +5:30, Tokyo = +9
        </p>
      </section>

      {/* About */}
      <section style={{ marginBottom: 32 }}>
        <SectionTitle>About</SectionTitle>
        <Row label="Version">
          <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--cream-dim)" }}>1.2.0</span>
        </Row>
        <Row label="Entries">
          <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--cream-dim)" }}>{count}</span>
        </Row>
        <Row label="Storage">
          <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--cream-faint)" }}>Local only</span>
        </Row>
        <Row label="Reminders" last>
          <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: isSubscribed ? "var(--teal)" : "var(--cream-faint)" }}>
            {isSubscribed ? "On" : "Off"}
          </span>
        </Row>
      </section>

      {/* Data */}
      <section style={{ marginBottom: 16 }}>
        <SectionTitle>Data</SectionTitle>
        <ActionBtn onClick={exportData} variant="default" testId="settings-export-btn">
          Export entries (JSON)
        </ActionBtn>
        <ActionBtn onClick={confirmClear} variant="danger" testId="settings-clear-btn">
          Clear all data
        </ActionBtn>
      </section>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
        <p style={{
          fontFamily: "var(--serif)", fontSize: 14,
          fontStyle: "italic", color: "var(--cream-faint)", lineHeight: 1.7,
        }}>
          "What did you contribute today<br />that no AI could replicate?"
        </p>
        <p style={{
          fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
          letterSpacing: "0.12em", color: "rgba(29,158,117,0.35)", marginTop: 12,
        }}>
          MYHYPHA.AI
        </p>
      </div>
    </div>
  );
}
